import React, { useState, useEffect } from 'react';
import { getPackagesByType, PACKAGE_TYPES } from '../services/packageService';
import { getUserActiveSubscription, createSubscription, hasUsedFreePackage } from '../services/subscriptionService';
import { initiateRazorpayPayment, calculateGST, formatAmount } from '../services/razorpayService';
import { createPaymentRecord, markPaymentSuccess, markPaymentFailed } from '../services/paymentService';
import LoadingSpinner from './LoadingSpinner';

// Predefined package features for comparison
const PREDEFINED_EMPLOYER_FEATURES = [
  'Unlimited Job Applications',
  '10 Days Job Validity',
  '15 Days Job Validity',
  '30 Days Job Validity',
  '60 Days Job Posting',
  '1 Job Posting',
  '2 Job Posting',
  '3 Job Posting',
  '10 Job Postings',
  'Premium Listing Position',
  '5 Pre Screened Leads',
  '20 Database Candidates'
];

const EmployerPackagePurchase = ({ user, userData, onPurchaseSuccess, showCurrentPackage = true, initialTab = 'job' }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [hasUsedFree, setHasUsedFree] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('🏢 EmployerPackagePurchase: Fetching EMPLOYER packages only');

      // Fetch employer packages
      const packagesResult = await getPackagesByType(PACKAGE_TYPES.EMPLOYER);
      if (packagesResult.success) {
        console.log(`✅ Found ${packagesResult.packages.length} employer packages`);
        setPackages(packagesResult.packages);
      } else {
        console.error('❌ Failed to fetch employer packages:', packagesResult.error);
      }

      // Fetch current subscription and free package usage
      if (user?.uid) {
        const subscriptionResult = await getUserActiveSubscription(user.uid);
        if (subscriptionResult.success && subscriptionResult.subscription) {
          console.log('✅ Found active subscription:', subscriptionResult.subscription.packageName);
          setCurrentSubscription(subscriptionResult.subscription);
        } else {
          console.log('ℹ️ No active subscription found');
        }

        const freeUsageResult = await hasUsedFreePackage(user.uid);
        if (freeUsageResult.success) {
          setHasUsedFree(freeUsageResult.hasUsedFree);
        } else {
          console.error('❌ Failed to check free package usage:', freeUsageResult.error);
        }
      }

    } catch (error) {
      console.error('❌ Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (pkg) => {
    if (!user?.uid) {
      alert('Please login to purchase a package');
      return;
    }

    setPurchasing(pkg.id);

    try {
      // Check for free package
      if (pkg.price === 0) {
        if (hasUsedFree) {
          alert('You have already used your free package trial.');
          setPurchasing(null);
          return;
        }

        console.log('🎁 Activating free package:', pkg.name);

        // Create subscription directly for free package
        const subscriptionData = {
          userId: user.uid,
          packageId: pkg.id,
          packageName: pkg.name,
          packageType: pkg.type,
          price: 0,
          duration: pkg.duration,
          features: pkg.features || [],
          maxJobPostings: pkg.maxJobPostings || 0,
          featuredJobs: pkg.featuredJobs || 0,
          candidateSearch: pkg.candidateSearch || false,
          analyticsAccess: pkg.analyticsAccess || false,
          maxAds: pkg.maxAds || pkg.postLimit || 0,
          userType: 'employer',
          paymentId: `FREE_${Date.now()}`,
          razorpayPaymentId: 'FREE_TIER',
          razorpayOrderId: 'FREE_TIER',
        };

        const subscriptionResult = await createSubscription(subscriptionData);

        if (subscriptionResult.success) {
          alert(`✅ Successfully activated ${pkg.name}!`);
          setHasUsedFree(true);
          fetchData(); // Refresh data

          if (onPurchaseSuccess) {
            onPurchaseSuccess(subscriptionResult.subscription);
          }
        } else {
          console.error('Failed to activate free package:', subscriptionResult.error);
          alert(`Failed to activate free package: ${subscriptionResult.error}`);
        }

        setPurchasing(null);
        return;
      }

      const totalAmount = pkg.price;
      console.log('💰 [EmployerPurchase] Price:', totalAmount);

      // Create payment record first
      const paymentData = {
        userId: user.uid,
        packageId: pkg.id,
        packageName: pkg.name,
        amount: totalAmount,
        currency: 'INR',
        gstAmount: calculateGST(totalAmount).gstAmount,
        totalAmount: calculateGST(totalAmount).totalAmount, // Recording total including GST
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
        employerName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        employerPhone: userData?.phone || '',
      };

      const paymentResult = await createPaymentRecord(paymentData);

      if (!paymentResult.success) {
        throw new Error('Failed to create payment record');
      }

      const firebasePaymentId = paymentResult.paymentId;

      // Calculate amount including GST for Razorpay
      const purchaseAmount = calculateGST(totalAmount).totalAmount;

      await initiateRazorpayPayment({
        amount: purchaseAmount,
        currency: 'INR',
        packageName: pkg.name,
        packageId: pkg.id,
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
        employerName: userData?.companyName || userData?.firstName,
        employerPhone: userData?.phone,
        onSuccess: async (paymentResponse) => {
          console.log('✅ [EmployerPurchase] Payment successful:', paymentResponse);

          // Update payment record
          await markPaymentSuccess(firebasePaymentId, paymentResponse);

          // Create subscription
          const subscriptionData = {
            userId: user.uid,
            packageId: pkg.id,
            packageName: pkg.name,
            packageType: pkg.type,
            price: pkg.price,
            duration: pkg.duration,
            features: pkg.features || [],
            maxJobPostings: pkg.maxJobPostings || 0,
            featuredJobs: pkg.featuredJobs || 0,
            candidateSearch: pkg.candidateSearch || false,
            analyticsAccess: pkg.analyticsAccess || false,
            maxAds: pkg.maxAds || pkg.postLimit || 0,
            userType: 'employer',
            paymentId: firebasePaymentId,
            razorpayPaymentId: paymentResponse.paymentId,
            razorpayOrderId: paymentResponse.orderId || null,
          };

          const subscriptionResult = await createSubscription(subscriptionData);

          if (subscriptionResult.success) {
            alert(`✅ Successfully purchased ${pkg.name}!`);
            fetchData();
            if (onPurchaseSuccess) {
              onPurchaseSuccess(subscriptionResult.subscription);
            }
          } else {
            alert('Payment successful but failed to activate subscription. Please contact support.');
          }

          setPurchasing(null);
        },
        onFailure: async (error) => {
          console.error('❌ [EmployerPurchase] Payment failed:', error);
          // Update payment record
          await markPaymentFailed(firebasePaymentId, error.error || 'Payment failed');

          if (!error.cancelled) {
            alert('Payment failed: ' + (error.error || 'Unknown error'));
          }
          setPurchasing(null);
        },
      });

    } catch (error) {
      console.error('Error purchasing package:', error);
      alert('Error initiating payment: ' + error.message);
      setPurchasing(null);
    }
  };

  const isCurrentPackage = (pkg) => {
    // Check if this package was the last purchased one
    if (!currentSubscription || !currentSubscription.purchaseHistory) return false;
    const lastPurchase = currentSubscription.purchaseHistory[currentSubscription.purchaseHistory.length - 1];
    return lastPurchase && lastPurchase.packageId === pkg.id;
  };

  const getSubscriptionStatus = () => {
    if (!currentSubscription) return null;

    const endDate = currentSubscription.endDate.toDate();
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

    return {
      packageName: currentSubscription.packageName,
      daysLeft,
      isExpiring: daysLeft <= 7,
      usageStats: currentSubscription.usageStats || {},
      maxJobPostings: currentSubscription.maxJobPostings || 0,
      featuredJobs: currentSubscription.featuredJobs || 0,
    };
  };

  if (loading) return <LoadingSpinner />;

  const subscriptionStatus = getSubscriptionStatus();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      
      {/* Header section */}
      <div className="text-center mb-12">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0E3A61] tracking-tight mb-3">
          Select Hiring Architecture
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base font-medium px-2">
          Acquire resource allocations to optimize your talent acquisition and expand professional visibility.
        </p>
      </div>

      {/* Current Subscription Status Panel (Restructured) */}
      {showCurrentPackage && subscriptionStatus && (
        <div className={`mb-10 p-6 rounded-3xl rounded-tr-[3.5rem] rounded-bl-[3.5rem] border ${subscriptionStatus.isExpiring
          ? 'bg-amber-50/50 border-amber-200 shadow-sm'
          : 'bg-emerald-50/40 border-emerald-200/80 shadow-sm'
          }`}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-3.5 h-3.5 rounded-full ${subscriptionStatus.isExpiring ? 'bg-amber-500 animate-pulse' : 'bg-[#508D0C]'}`}></div>
                <h3 className={`text-lg font-black ${subscriptionStatus.isExpiring ? 'text-amber-900' : 'text-[#0E3A61]'}`}>
                  Active Operational Plan: <span className="underline decoration-wavy decoration-[#E2822F] underline-offset-4">{subscriptionStatus.packageName}</span>
                </h3>
                {subscriptionStatus.isExpiring && (
                  <span className="ml-2 px-2.5 py-0.5 bg-amber-500 text-white rounded-full text-[10px] font-bold uppercase tracking-wider">
                    Expiring Soon
                  </span>
                )}
              </div>

              {/* Usage stats columns inside active panel */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Days remaining */}
                <div className="bg-white border border-slate-200/60 rounded-2xl p-4 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Remaining Cycle</p>
                  <p className={`text-2xl font-black ${subscriptionStatus.isExpiring ? 'text-amber-600' : 'text-[#508D0C]'}`}>
                    {subscriptionStatus.daysLeft > 0 ? `${subscriptionStatus.daysLeft} Days` : 'Expired'}
                  </p>
                </div>

                {/* Job postings */}
                <div className="bg-white border border-slate-200/60 rounded-2xl p-4 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Allocated Posts Logged</p>
                  <p className="text-2xl font-black text-[#0E3A61] mb-2">
                    {subscriptionStatus.usageStats.jobPostingsUsed || 0} <span className="text-sm font-semibold text-slate-400">/ {subscriptionStatus.maxJobPostings === 999999 ? '∞' : (subscriptionStatus.maxJobPostings || 0)}</span>
                  </p>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-[#0E3A61] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${subscriptionStatus.maxJobPostings === 999999 ? 0 : Math.min(((subscriptionStatus.usageStats.jobPostingsUsed || 0) / (subscriptionStatus.maxJobPostings || 1)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Featured slots */}
                <div className="bg-white border border-slate-200/60 rounded-2xl p-4 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Featured Slots Logged</p>
                  <p className="text-2xl font-black text-amber-600 mb-2">
                    {subscriptionStatus.usageStats.featuredJobsUsed || 0} <span className="text-sm font-semibold text-slate-400">/ {subscriptionStatus.featuredJobs === 999999 ? '∞' : (subscriptionStatus.featuredJobs || 0)}</span>
                  </p>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${subscriptionStatus.featuredJobs === 999999 ? 0 : Math.min(((subscriptionStatus.usageStats.featuredJobsUsed || 0) / (subscriptionStatus.featuredJobs || 1)) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-[9px] font-semibold text-slate-400 mt-1.5">⭐ Subset of primary allocation</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Package Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {packages.map((pkg, index) => {
          const recommended = index === 1;
          const current = isCurrentPackage(pkg);
          
          return (
            <div
              key={pkg.id}
              className={`relative bg-white rounded-3xl rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-md border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.03] flex flex-col justify-between ${
                recommended
                  ? 'border-[#0E3A61] shadow-[#0E3A61]/10 ring-4 ring-[#0E3A61]/5'
                  : 'border-slate-200/80 hover:border-slate-300'
              }`}
            >
              {/* Card Badges */}
              {recommended && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-[#0E3A61] to-[#E2822F] text-white px-5 py-1.5 rounded-full text-xs font-black tracking-wide shadow-md">
                    EXECUTIVE CHOICE
                  </span>
                </div>
              )}

              {current && (
                <div className="absolute -top-4 right-6 z-10">
                  <span className="bg-[#508D0C] text-white px-3 py-1 rounded-full text-[10px] font-black tracking-wider shadow-sm">
                    ✓ PREVIOUS ALLOCATION
                  </span>
                </div>
              )}

              {/* Main Card Content */}
              <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between">
                
                {/* Header info */}
                <div>
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-black text-slate-800 mb-4">{pkg.name}</h3>

                    {/* Price structure */}
                    {pkg.price > 0 ? (
                      <div className="bg-gradient-to-br from-slate-50 to-blue-50/20 border border-slate-100 rounded-2xl p-4 mb-2">
                        <div className="flex justify-between items-center text-xs font-semibold text-slate-500 mb-1.5">
                          <span>Net Rate:</span>
                          <span className="font-bold text-slate-700">{formatAmount(pkg.price)}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs font-semibold text-slate-500 mb-1.5">
                          <span>Regulatory Tax (18%):</span>
                          <span className="font-bold text-slate-700">{formatAmount(calculateGST(pkg.price).gstAmount)}</span>
                        </div>
                        <div className="border-t border-slate-200/60 pt-2.5 mt-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-bold text-[#0E3A61] uppercase tracking-wider">Aggregate Outlay:</span>
                            <span className="text-2xl font-black bg-gradient-to-r from-[#0E3A61] to-[#E2822F] bg-clip-text text-transparent">
                              {formatAmount(calculateGST(pkg.price).totalAmount)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 rounded-2xl p-5 mb-2">
                        <div className="text-center">
                          <span className="text-2xl font-black text-[#508D0C] block mb-0.5">COMPLIMENTARY</span>
                          <span className="text-xs text-emerald-800 font-bold uppercase tracking-wider">Zero Payment Required</span>
                        </div>
                      </div>
                    )}

                    <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">
                      Term of Validity: {pkg.duration} Days
                    </span>
                  </div>

                  {pkg.description && (
                    <p className="text-xs text-slate-500 font-medium text-center mb-6 leading-relaxed">
                      {pkg.description}
                    </p>
                  )}

                  {/* Features list */}
                  <div className="mb-6">
                    {pkg.features && pkg.features.length > 0 && (
                      <div className="border border-slate-200/80 rounded-2xl overflow-hidden shadow-inner">
                        <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200/80">
                          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">✓ Plan Capabilities ({pkg.features.length})</p>
                        </div>
                        <div className="p-2 space-y-1 bg-white max-h-[160px] overflow-y-auto">
                          {pkg.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center justify-between px-3 py-1.5 bg-slate-50/50 rounded-lg">
                              <span className="text-xs font-bold text-slate-700">{feature}</span>
                              <svg className="w-4 h-4 text-[#508D0C] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Purchase Button */}
                <div>
                  <button
                    onClick={() => handlePurchase(pkg)}
                    disabled={purchasing === pkg.id || (pkg.price === 0 && hasUsedFree)}
                    className={`w-full py-3.5 px-6 rounded-xl font-bold text-sm uppercase tracking-wider transition-all duration-300 transform active:scale-95 shadow-md hover:shadow-lg ${
                      purchasing === pkg.id || (pkg.price === 0 && hasUsedFree)
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed shadow-none hover:shadow-none transform-none'
                        : recommended
                          ? 'bg-gradient-to-r from-[#0E3A61] to-[#E2822F] text-white hover:opacity-95'
                          : 'bg-slate-800 text-white hover:bg-slate-700'
                    }`}
                  >
                    {purchasing === pkg.id
                      ? 'Processing...'
                      : current
                        ? 'Re-purchase Allocation'
                        : pkg.price === 0
                          ? hasUsedFree
                            ? 'Already Claimed'
                            : 'Claim Free Trial'
                          : 'Authorize Acquisition'
                    }
                  </button>

                  {currentSubscription && (
                    <p className="text-[10px] text-center text-slate-400 font-bold uppercase tracking-wider mt-2.5">
                      ✨ Purchased benefits will merge with the active operational plan.
                    </p>
                  )}
                </div>

              </div>
            </div>
          );
        })}
      </div>

      {packages.length === 0 && (
        <div className="text-center py-16 bg-white border border-slate-200/80 rounded-3xl p-8 max-w-md mx-auto">
          <div className="text-slate-300 text-5xl mb-3">📦</div>
          <h3 className="text-lg font-black text-[#0E3A61] mb-1">No Hiring Plans Available</h3>
          <p className="text-slate-500 text-sm font-medium">
            No employer operational packages are currently available. Please check back later.
          </p>
        </div>
      )}
    </div>
  );
};

export default EmployerPackagePurchase;
